import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

// Components
import { Button, Loader, Modal, Typography } from 'components';
import {
  ButtonBar,
  ModalCopyTable,
  ModalCopyWidget,
    WidgetTable,
  WidgetTableProvider
} from 'components/machine-health';
import SaveButton from './SaveButton';

// Types
import { ModalSize } from 'types';
import { WidgetTableDataItem, WidgetTableDropdownItem } from 'types/machine-health';
import { WidgetType } from 'types/protein';

// Constants
import { WidgetTableTagColumnConfigs } from 'constants/machineConfig';

// Hooks
import { useMachine } from 'hooks';
import { useLanguage } from 'providers';

// API
import {
  useGetBusinessUnitMasterTagListQuery,
  useGetMachineWidgetDataQuery,
  useUpdateMachineConfiguratorDataMutation,
  useCreateMachineConfigurationDataMutation,
  useGetMachineDataAnalysisTagsQuery,
  useUploadTagsImageMutation,
  useDeleteTagsImageMutation
} from 'api';

// Helpers
import { mapBusinessUnitId } from 'common/helpers/v1/machine';
import { ToastMsg } from 'common/components/Toast';
import { set } from 'lodash';
import { UploadTagsImageData } from '../WidgetTable/WidgetTableContext';

const StyledTableWrapper = styled.div`
  padding: 0 3rem 3rem;
`;
export interface NewWidgetData {
  name: string;
  names?: {
    en: string;
    fr?: string;
  };
  label: string | string[];
  parentId?: string;
  widgetClass: string;
  widgetType: WidgetType | string;
  editable?: boolean;
  deletable?: boolean;
  active?: boolean;
}
interface Props {
  addImageRow?: boolean;
  hideInactive?: boolean;
  machineId: string;
  widgetId?: string;
  newWidgetData?: NewWidgetData;
  isEditAdminPopupOpen: boolean;
  setIsEditAdminPopupOpen: (x: boolean) => void;
  showUploadImageRow?: boolean;
}

const AdminCardEditButton = ({
  addImageRow,
  hideInactive,
  machineId,
  widgetId,
  newWidgetData,
  isEditAdminPopupOpen,
  setIsEditAdminPopupOpen,
  showUploadImageRow
}: Props): JSX.Element => {
  const { machine, isLoading: isLoadingMachine, error: errorMachine } = useMachine();
  const { languageId } = useLanguage();
  const {
    data: machineTags,
    isFetching: isFetchingMachineTags,
    error: errorMachineTags
  } = useGetBusinessUnitMasterTagListQuery({
    businessUnitId: mapBusinessUnitId(machine?.businessUnit) || 3,
    languageId
  });

  const {
    data: tagValues,
    isFetching: isFetchingTagValues,
    error: errorTagValues
  } = useGetMachineDataAnalysisTagsQuery({ machineId, numbersOnly: 'false' });

  const {
    data: widgetData,
    isFetching: isFetchingWidgetData,
    error: errorWidgetData
  } = useGetMachineWidgetDataQuery(
    {
      machineId,
      widgetId: widgetId as string,
      includeTagValues: true,
      showInactive: !hideInactive,
      languageId: languageId
    },
    { skip: !widgetId }
  );

  const defaultWidgetData = useMemo<WidgetTableDataItem>(() => {
    if (!newWidgetData) return undefined as unknown as WidgetTableDataItem;
    return {
      id: uuidv4(),
      name: newWidgetData.name,
      label: newWidgetData.label,
      widgetClass: newWidgetData.widgetClass,
      widgetType: newWidgetData.widgetType as WidgetType,
      active: newWidgetData.active ?? true,
      editable: newWidgetData.editable ?? true,
      deleteable: newWidgetData.deletable ?? false,
      members: []
    };
  }, [newWidgetData]);

  const effectiveWidgetData = widgetData ?? (!widgetId && defaultWidgetData ? defaultWidgetData : undefined);

  const [updateProductProcessingData, { isLoading: isUpdating, error: errorUpdate }] =
    useUpdateMachineConfiguratorDataMutation();

  const [createMachineConfiguration, { isLoading: isCreating, error: errorCreate }] =
    useCreateMachineConfigurationDataMutation();


  const [uploadTagsImage] = useUploadTagsImageMutation();
  const [deleteTagsImage] = useDeleteTagsImageMutation();

  const [isDirty, setIsDirty] = useState(false);
  const [showModalCopyWidget, setShowModalCopyWidget] = useState(false);
  const [showModalCopyTable, setShowModalCopyTable] = useState(false);
  const [copyMachineId, setCopyMachineId] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // State to store the uploaded file
  const [isDeleteImage, setIsDeleteImage] = useState(false);

  const tagList = useMemo(() => {
    const tags: WidgetTableDropdownItem[] = [];

    machineTags?.forEach((tag) => {
      const values = [];
      const foundValue = tagValues?.find((tagValue) =>
        tagValue?.tagId?.toLowerCase() === tag?.id?.toLowerCase()
      );
      foundValue && values.push(foundValue);

      tags.push({
        id: tag.id as string,
        ...(tag.friendlyName ? { name: tag.friendlyName as string } : {}),
        type: WidgetType.State, // TODO this needs data from the backend
        label: (tag.friendlyName || tag.id) as string,
        values
      });
    });

    tags
      .sort((option1, option2) => {
        const typedOption1 = option1;
        const option1Values = typedOption1.values as { value: number }[];
        const typedOption2 = option2;
        const option2Values = typedOption2.values as { value: number }[];

        if (option1Values.length && !option2Values.length) {
          return -1;
        }
        if (!option1Values.length && option2Values.length) {
          return 1;
        }
        return 0;
      })
      .sort((option1, option2) => {
        const typedOption1 = option1;
        const option1Values = typedOption1.values as { value: number }[];
        const typedOption2 = option2;
        const option2Values = typedOption2.values as { value: number }[];
        if (
          (option1Values[0]?.value != undefined && option2Values[0]?.value != undefined) ||
          (option1Values[0]?.value == undefined && option2Values[0]?.value == undefined)
        ) {
          const option1Id = (typedOption1.id as string).toLowerCase();
          const option2Id = (typedOption2.id as string).toLowerCase();
          if (option1Id < option2Id) {
            return -1;
          }
          if (option1Id > option2Id) {
            return 1;
          }
          return 0;
        }
        return 0;
      });

    return tags;
  }, [machineTags, tagValues]);

  const isLoading =
    isFetchingMachineTags ||
    isFetchingWidgetData ||
    isUpdating ||
    isCreating ||
    isFetchingTagValues ||
    isLoadingMachine;

  const error =
    errorMachine || errorWidgetData || errorUpdate || errorCreate || errorTagValues || errorMachineTags;

  useEffect(() => {
    setIsDirty(false);
  }, []);

  const handleFileUpdate = (file: File | null) => {
    setUploadedFile(file); // Correctly update the state with the received file
  };

  const deleteTagImageAPI = async(tagId:string) =>{
    try {
      await deleteTagsImage({
        machineId,
        widgetId: tagId
      }).unwrap();
    } catch (error) {
      console.error('Error tag deleting image:', error);
    }
  }

  const uploadTagImageAPI = async(tagId:string, tagImageFile:File) =>{
    try {
        await uploadTagsImage({
        machineId,
        widgetId: tagId,
        file: tagImageFile
      }).unwrap();
      } catch (error) {
        console.error('Error tag update image:', error);
      }
  }

  const saveInfo = async (data: WidgetTableDataItem[], imageData?: UploadTagsImageData[]) => {
    try {
      const updatedWidget = data[0]?.members?.[0];

      const imageTagDataInfo = imageData as UploadTagsImageData[];

      /** upload and delete tag image start */
        const imagePromises = imageTagDataInfo.map((tagData) => {
          if (tagData?.markedForDeleted) {
            return deleteTagImageAPI(tagData?.id);
          } else if (tagData?.imageFile) {
            return uploadTagImageAPI(tagData?.id, tagData?.imageFile);
          }
          return Promise.resolve();
        });
        await Promise.all(imagePromises);
      /** upload and delete tag image end */

      if (!updatedWidget) {
        return;
      }

      if (widgetId) {
        await updateProductProcessingData({
          machineId,
          widget: updatedWidget,
          languageId
        }).unwrap();
      } else {
        const configuration = {
          ...updatedWidget,
          ...(newWidgetData?.parentId && { parentId: newWidgetData.parentId }),
          ...(newWidgetData?.widgetClass && { widgetClass: newWidgetData.widgetClass }),
          ...(newWidgetData?.label && { label: newWidgetData.label }),
          ...(newWidgetData?.names && { names: newWidgetData.names })
        };
        await createMachineConfiguration({
          machineId,
          configuration,
          languageId
        }).unwrap();
      }

      setUploadedFile(null);
      setIsDirty(false);
      setIsEditAdminPopupOpen(false);

      ToastMsg({ heading: 'Success', type: 'success', message: 'Data updated successfully' });
    } catch (error) {
      console.error('Error during saveInfo:', error);
      ToastMsg({ heading: 'Error', type: 'error', message: 'Failed to update data' });
    }
  };

  const onCopyFromSelectedMachine = (id: string) => {
    setCopyMachineId(id);
    setShowModalCopyWidget(false);
    setShowModalCopyTable(true);
  };

  const handleModalClose = (): void => {
    setIsEditAdminPopupOpen(false);
    setIsDeleteImage(false); // Reset the delete image state
    // we don't need to do anything else here, ...
    // ... if we don't make an update call the original data will not update ...
    // ... the next modal open will reset with original data
  };

  if (error) {
    console.error(error);
    <Typography color="negativeRed">Failed to load data</Typography>;
  }

  return (
    <WidgetTableProvider>
      <Modal
        title={'Customize Machine States'}
        visible={isEditAdminPopupOpen}
        onClose={() => handleModalClose()}
        size={ModalSize.LARGE}
      >
        <StyledTableWrapper>
          <ButtonBar title="">
            <Button width="fit-content" onClick={() => setShowModalCopyWidget(true)}>
              Copy Widget from Machine
            </Button>
            <SaveButton isDirty={isDirty} saveCallBack={saveInfo} />
          </ButtonBar>
          <ModalCopyWidget
            onSelectMachine={onCopyFromSelectedMachine}
            setVisible={setShowModalCopyWidget}
            visible={showModalCopyWidget}
          />
          <ModalCopyTable
            closeModal={() => setShowModalCopyTable(false)}
            copyDestinationItem={effectiveWidgetData}
            copyMachineId={copyMachineId}
            copyWidgetType={effectiveWidgetData?.widgetType as WidgetType}
            setIsDirty={setIsDirty}
            setVisible={setShowModalCopyTable}
            visible={showModalCopyTable}
          />
          {isLoading ? (
            <Loader />
          ) : effectiveWidgetData ? (
            <WidgetTable
              columnConfigs={WidgetTableTagColumnConfigs}
              data={[effectiveWidgetData]}
              displayOnlyRows={
                addImageRow
                  ? [
                      {
                        id: 'machine-image-id',
                        key: uuidv4(),
                        name: 'Image',
                        tagType: 'Machine Image',
                        widgetType: WidgetType.MachineImage
                      }
                    ]
                  : null
              }
              parent={{
                ...effectiveWidgetData,
                name: undefined
              }}
              setIsDirty={setIsDirty}
              shouldRenderTags
              tags={tagList}
              showUploadImageRow={showUploadImageRow}
              onFileUpdate={handleFileUpdate}
              isDeleteImage={isDeleteImage}
              setIsDeleteImage={setIsDeleteImage}
            />
          ) : (
            <Typography>No Data</Typography>
          )}
        </StyledTableWrapper>
      </Modal>
    </WidgetTableProvider>
  );
};

export default AdminCardEditButton;
